import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';

/*
 * Props.
 */

export enum ComposerBannerThemesEnum {
  BLUE = 'BLUE',
  ORANGE = 'ORANGE',
}

interface ComposerBannerBaseProps {
  bannerTheme: ComposerBannerThemesEnum;
  shouldShowWarningIcon?: boolean;
  hasSynchronousTimeline?: boolean;
  onDismiss?: Handler;
}

/*
 * Style.
 */

interface StyledWrapperDivProps {
  $hasSynchronousTimeline?: boolean;
}
const StyledWrapperDiv = styled.div<StyledWrapperDivProps>`
  position: absolute;
  display: flex;
  justify-content: center;
  bottom: calc(100% + 16px);
  width: 100%;
  pointer-events: none;

  ${(p) =>
    p.$hasSynchronousTimeline &&
    css`
      margin-bottom: -59px;
    `}
`;

interface BannerStyleProps {
  $theme: ComposerBannerThemesEnum;
}

const StyledBannerDiv = styled.div<BannerStyleProps>`
  pointer-events: auto;
  display: flex;
  align-items: center;
  padding: 0 5px;

  border-radius: 18px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};

  ${(p) => getBannerStyle(p.$theme)}

  margin: 0 8px;
`;

const StyledDismissButton = styled.div<BannerStyleProps>`
  flex-shrink: 0;
  border-radius: 13px;

  padding: 5px;
  margin-left: 3px;

  &:hover {
    ${(p) => getDismissButtonStyle(p.$theme)}
  }
`;

const StyledWarningIconDiv = styled.div`
  flex-shrink: 0;
  margin-left: 10px;
  color: ${(p) => p.theme.palette.orange.shade40};
`;

function getBannerStyle(theme: ComposerBannerThemesEnum) {
  switch (theme) {
    case ComposerBannerThemesEnum.ORANGE:
      return css`
        color: ${(p) => p.theme.palette.orange.shade40};
        background-color: ${(p) => p.theme.foreground};
        ${(p) => boxShadow(p.theme, `0 2px 10px 0 rgba(0, 0, 0, 0.2)`)};
      `;

    case ComposerBannerThemesEnum.BLUE:
    default:
      return css`
        color: ${(p) => p.theme.palette.blue.shade40};
        background-color: ${(p) => p.theme.palette.blue.shade20};
        ${(p) => boxShadow(p.theme, `0 2px 6px 0 ${p.theme.alphas.black20}`)};
      `;
  }
}

function getDismissButtonStyle(theme: ComposerBannerThemesEnum) {
  switch (theme) {
    case ComposerBannerThemesEnum.ORANGE:
      return css`
        color: ${(p) => p.theme.palette.orange.shade50};
        background-color: ${(p) => p.theme.greys.shade20};
      `;

    case ComposerBannerThemesEnum.BLUE:
    default:
      return css`
        color: ${(p) => p.theme.greys.white};
        background-color: ${(p) => p.theme.palette.blue.shade40};
      `;
  }
}

/*
 * Component.
 */

export const ComposerBannerBubble: FC<PropsWithChildren<ComposerBannerBaseProps>> = (props) => (
  <StyledWrapperDiv $hasSynchronousTimeline={props.hasSynchronousTimeline}>
    <StyledBannerDiv $theme={props.bannerTheme}>
      {maybeRenderWarningIcon(props)}
      {props.children}
      {maybeRenderDismiss(props)}
    </StyledBannerDiv>
  </StyledWrapperDiv>
);

/*
 * Helpers.
 */

function maybeRenderWarningIcon(props: ComposerBannerBaseProps) {
  if (!props.shouldShowWarningIcon) {
    return null;
  }

  return (
    <StyledWarningIconDiv>
      <Icon name="warningFilledSmall" />
    </StyledWarningIconDiv>
  );
}

function maybeRenderDismiss(props: ComposerBannerBaseProps) {
  if (!props.onDismiss) {
    return null;
  }

  return (
    <ChromelessButton onClick={props.onDismiss}>
      <StyledDismissButton $theme={props.bannerTheme}>
        <Icon name="closeSmall" />
        <VisuallyHidden>
          <Trans id="composerbanner-close">Close</Trans>
        </VisuallyHidden>
      </StyledDismissButton>
    </ChromelessButton>
  );
}
